import { CameraIcon, X } from "lucide-react";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import heic2any from "heic2any";
import { showError, showSuccess } from "../../../../../util/ShowStatus";
import { formatTimestamp } from "../../helper/helperFunctions";

interface PhotoUploadProps {
  label: any; // Changed to 'any' to allow for JSX elements
  name: string;
  images: Record<string, File>;
  isUploadInProgress: boolean;
  setIsUploadInProgress: (value: boolean) => void;
  setImages: (files: Record<string, File>) => void;
  fileValues: any[];
  setFileValues: React.Dispatch<React.SetStateAction<any[]>>;
  timeStampLocationValues: {
    locations: Array<{
      photoKey: string;
      address: string;
      coordinates?: { latitude: number; longitude: number };
    }>;
    timeStamps: Array<{ photoKey: string; timestamp: string }>;
  };
  setTimeStampLocationValues: (values: any) => void;
  classname?: string;
  maxImages?: number;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  label,
  name,
  images,
  isUploadInProgress,
  setIsUploadInProgress,
  setImages,
  fileValues,
  setFileValues,
  timeStampLocationValues,
  setTimeStampLocationValues,
  classname,
  maxImages = 1,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);

  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/heic",
    "image/heif",
    "image/webp",
  ];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  // Generate a unique key based on the label
  const getPhotoKey = () => {
    return name.toLowerCase().replace(/\s+/g, "-");
  };

  const convertHeicToJpeg = async (file: File): Promise<File> => {
    const fileType =
      file.type || `image/${file.name.split(".").pop()?.toLowerCase()}`;
    if (fileType === "image/heic" || fileType === "image/heif") {
      try {
        const conversionResult = (await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        })) as Blob;

        return new File(
          [conversionResult],
          file.name.replace(/\.[^/.]+$/, ".jpg"),
          {
            type: "image/jpeg",
            lastModified: new Date().getTime(),
          }
        );
      } catch (error) {
        console.error("HEIC conversion failed:", error);
        throw new Error("Failed to convert HEIC image");
      }
    }
    return file;
  };

  const validateFile = (file: File): boolean => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_TYPES.includes(file.type.toLowerCase()) && file.type !== "") {
      showError(
        `Unsupported file type: ${file.type}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    } else if (
      fileExtension &&
      !ALLOWED_TYPES.includes(`image/${fileExtension}`) &&
      file.type === ""
    ) {
      showError(
        `Unsupported file type: ${fileExtension}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    } else if (!fileExtension && file.type === "") {
      showError(
        `Unsupported file type: ${fileExtension}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      showError(
        `File is too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`,
        toast
      );
      return false;
    }

    return true;
  };

  const getAddressFromCoordinates = async (
    latitude: number,
    longitude: number
  ): Promise<string> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
            "User-Agent": "PhotoUploadApp/1.0",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch address");
      }

      const data = await response.json();
      const address = formatAddress(data.address);
      return address;
    } catch (error) {
      console.error("Error getting address:", error);
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    }
  };

  const formatAddress = (addressData: any): string => {
    const components = [];

    if (addressData.suburb) components.push(addressData.suburb);
    else if (addressData.neighbourhood)
      components.push(addressData.neighbourhood);
    else if (addressData.quarter) components.push(addressData.quarter);

    if (addressData.state_district) components.push(addressData.state_district);
    else if (addressData.town) components.push(addressData.town);
    else if (addressData.village) components.push(addressData.village);

    if (addressData.state) components.push(addressData.state);
    if (addressData.country) components.push(addressData.country);
    if (addressData.postcode) components.push(addressData.postcode);

    return components.join(", ");
  };

  const getCurrentLocation = async (): Promise<{
    address: string;
    coordinates: { latitude: number; longitude: number };
  } | null> => {
    if (!navigator.geolocation) {
      showError("Geolocation is not supported by your browser", toast);
      return null;
    }

    setIsGettingLocation(true);

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        }
      );

      const coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      const address = await getAddressFromCoordinates(
        coordinates.latitude,
        coordinates.longitude
      );

      return {
        address,
        coordinates,
      };
    } catch (error: any) {
      console.error("Geolocation error:", error);
      return null;
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || !event.target.files[0]) return;

    const originalFile = event.target.files[0];
    if (!validateFile(originalFile)) {
      event.target.value = "";
      return;
    }

    setIsUploadInProgress(true);

    try {
      const locationData = await getCurrentLocation();
      // Ensure timestamp is in local time
      const now = new Date();
      const timestamp = formatTimestamp(
        new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          now.getHours(),
          now.getMinutes(),
          now.getSeconds(),
          now.getMilliseconds()
        )
      );
      const photoKey = getPhotoKey();

      setTimeStampLocationValues((prev: any) => {
        const filteredLocations = prev.locations.filter(
          (loc: any) => loc.photoKey !== photoKey
        );
        const filteredTimeStamps = prev.timeStamps.filter(
          (ts: any) => ts.photoKey !== photoKey
        );

        if (!locationData) {
          return {
            locations: filteredLocations,
            timeStamps: filteredTimeStamps,
          };
        }
        return {
          locations: [
            ...filteredLocations,
            {
              photoKey,
              address: locationData.address,
              coordinates: locationData.coordinates,
            },
          ],
          timeStamps: [...filteredTimeStamps, { photoKey, timestamp }],
        };
      });

      const file = await convertHeicToJpeg(originalFile);

      // Update images state
      setImages({ ...images, [photoKey]: file });

      // Update fileValues state
      setFileValues((prev: any[]) => {
        const existingIndex = prev.findIndex(
          (item) => item.photoKey === photoKey
        );
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            photoKey,
            file,
            label,
            timestamp,
            location: locationData,
          };
          return updated;
        } else {
          return [
            ...prev,
            {
              photoKey,
              file,
              label,
              timestamp,
              location: locationData,
            },
          ];
        }
      });

      showSuccess("Photo uploaded successfully!", toast);
    } catch (error) {
      showError("Failed to process image. Please try again.", toast);
      console.error("Image processing error:", error);
    } finally {
      setIsUploadInProgress(false);
      event.target.value = "";
    }
  };

  const handleRemoveImage = () => {
    const photoKey = getPhotoKey();
    const newImages = { ...images };
    delete newImages[photoKey];
    setImages(newImages);

    setTimeStampLocationValues((prev: any) => ({
      locations: prev.locations.filter((loc: any) => loc.photoKey !== photoKey),
      timeStamps: prev.timeStamps.filter((ts: any) => ts.photoKey !== photoKey),
    }));

    setFileValues((prev: any[]) =>
      prev.filter((item) => item.photoKey !== photoKey)
    );
  };

  const triggerFileInput = () => {
    if (isUploadInProgress) {
      showError("Please wait until the current upload completes", toast);
      return;
    }

    if (maxImages === 0) {
      showError("Maximum number of photos reached", toast);
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const photoKey = getPhotoKey();
  const currentImage = images[photoKey];

  return (
    <>
      <Toast ref={toast} />
      <div
        className={`tw-flex tw-flex-col tw-gap-1 tw-w-full tw-flex-1 ${classname}`}
      >
        <label className="leak-label">{label}</label>

        <div
          className={`tw-flex tw-justify-center tw-items-center tw-min-h-[100px] tw-border-solid tw-border-[1px] tw-border-[#D0D5DD] tw-rounded-lg tw-relative`}
        >
          <div
            className="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center tw-cursor-pointer"
            onClick={triggerFileInput}
          >
            {!currentImage ? (
              isGettingLocation ? (
                <div className="tw-text-sm tw-text-gray-500">
                  Getting location...
                </div>
              ) : (
                <CameraIcon className="tw-text-xl tw-text-gray-500" />
              )
            ) : (
              <div className="tw-flex tw-flex-wrap tw-gap-2 tw-p-2 tw-w-full">
                <div className="tw-relative tw-w-20 tw-h-20 tw-bg-gray-200 tw-rounded-lg">
                  <Image
                    src={URL.createObjectURL(currentImage)}
                    alt={`Uploaded ${label}`}
                    className="tw-w-20 tw-h-20 tw-inline-flex tw-object-contain"
                  />
                  <div className="tw-absolute tw-bottom-1 tw-left-1 tw-bg-black/50 tw-text-white tw-text-[8px] tw-px-1 tw-py-0.5 tw-rounded">
                    {currentImage.name.split(".").pop()?.toUpperCase()} •{" "}
                    {(currentImage.size / 1024).toFixed(1)} KB
                  </div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    className="tw-absolute tw-top-1 tw-right-1 tw-bg-gray-500 tw-text-white tw-rounded-full tw-p-1 tw-text-xs tw-flex tw-items-center tw-justify-center"
                    disabled={isUploadInProgress}
                  >
                    <X size={12} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="tw-hidden"
          onChange={handleFileChange}
          accept="image/*"
          capture="environment"
        />
      </div>
    </>
  );
};

export default PhotoUpload;
